/**
 * @file AssetDrawer.js
 * @description Side drawer containing the details pertaining to an
 * asset. Offers navigation into sub-panels like History, Portals,
 * Notifications, and Templates.
 */
import { useState, useEffect, useRef, useContext } from 'react';
import classNames from 'classnames';
import { useAuthContext, useUploadContext } from '../../store/Contexts';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import {
    formatFileSize,
    getFileExtension,
    getFileCategory,
    processFileName,
    getVideoRuntime,
    getAudioLength,
    getImageDimensions,
    canRenderAssetPreviewInBrowser,
} from '../helpers/FileUtils';
import AssetDetailsPanel from './components/AssetDetailsPanel';
import AssetHistoryPanel from './components/AssetHistoryPanel';
import AssetPortalsPanel from './components/AssetPortalsPanel';
import AssetReadOnlyPanel from './components/AssetReadOnlyPanel';
import AssetNotificationsPanel from './components/AssetNotificationsPanel';
import AssetTemplatesPanel from './components/AssetTemplatesPanel';
import FullscreenDisplay from './shared/FullscreenDisplay';
import { isValidFileTypeAndSize } from '../../utils/validate';
import {
    deleteAsset,
    removeAsset,
    getClientOptions,
} from '../../services/clientService';
import { submitAssetUpdate } from '../../services/assetLibraryService';
import { clientApiHttp } from '../../services/httpService';
import styles from './AssetDrawer.module.scss';
import {
    EventDataTypeEnum,
    ClientOptionEnum,
    LogEventType,
    SecurityAttributes,
} from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { getText } from '../../i18n';

const drawerStyles = css`
    position: fixed;
    top: 0;
    right: 0;
    height: 100%;
    width: 494px;
    background: var(--iNGAGED-Neutral-Color7);
    z-index: 1000;
    box-shadow: -2px 0 2px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 0 100px;
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const overlayStyles = css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.4);
    z-index: 999;
`;

const AssetDrawer = ({
    onClose,
    onSaveOrDelete,
    assetId,
    isMyAssets = false,
    isPortalActions = false,
    isGlobalAssets = false,
    eventContextDataTypeId = null,
    eventContextId = null,
    eventContextName = null,
    isResourceLibrary = false,
    isResourceLibraryReadOnly = false,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [asset, setAsset] = useState({});
    const [originalAsset, setOriginalAsset] = useState({});
    const [replacementFile, setReplacementFile] = useState(null);
    const [replacementImageSrc, setReplacementImageSrc] = useState(null);
    const [isHistoryPanelVisible, setIsHistoryPanelVisible] = useState(false);
    const [isPortalsPanelVisible, setIsPortalsPanelVisible] = useState(false);
    const [isNotificationsPanelVisible, setIsNotificationsPanelVisible] =
        useState(false);
    const [isTemplatesPanelVisible, setIsTemplatesPanelVisible] =
        useState(false);
    const [isReadOnlyAsset, setIsReadOnlyAsset] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { showModal } = useContext(ModalContext);
    const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
    const scrollableRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [fullscreenImageUrl, setFullscreenImageUrl] = useState('');
    const [fullscreenMediaType, setFullscreenMediaType] = useState('image');
    const [allowAssetSharing, setAllowAssetSharing] = useState(false);
    const isMobileView = window.innerWidth <= 768;
    const { isLoggedIn, user, multitenantFlag, hasSecurityAttribute } =
        useAuthContext();
    const { enqueueUpload } = useUploadContext();
    const isAssetLibraryReadOnly = hasSecurityAttribute([
        SecurityAttributes.AssetLibraryReadOnly,
    ]);
    const canEditAsset = isResourceLibrary
        ? !isResourceLibraryReadOnly
        : !isReadOnlyAsset && !isAssetLibraryReadOnly;

    /**
     * Ref forwarded to TagInput so we can call tagInputRef.current.flush()
     * before saving. This commits any text the user has typed into the tag
     * input but not yet confirmed with Enter/Space/comma.
     */
    const tagInputRef = useRef(null);

    useEffect(() => {
        loadAsset();
    }, []);

    useEffect(() => {
        if (!isMyAssets) {
            return;
        }
        const loadSharingOption = async () => {
            try {
                const data = await getClientOptions(0, [
                    ClientOptionEnum.AllowAssetSharing,
                ]);
                const option = data.find(
                    item =>
                        item.clientOptionId ===
                        ClientOptionEnum.AllowAssetSharing,
                );
                if (option) {
                    setAllowAssetSharing(option.value !== 'false');
                }
            } catch (error) {
                console.error('Failed to fetch asset sharing option', error);
            }
        };
        loadSharingOption();
    }, [isMyAssets]);

    const loadAsset = async (bustCache = false) => {
        try {
            // Always fetch fresh from the API directly to avoid stale cache issues.
            // fetchAssetByID uses a 60s TTL cache which causes saved tags to appear
            // missing when the drawer is reopened shortly after a save.
            const tenantId = +window.localStorage.getItem('tenantId');
            const response = await clientApiHttp.get(
                `v2/client/${tenantId}/asset/${assetId}?includeHistory=true`,
            );
            const fetchedAsset = response.data;
            fetchedAsset.assetTags =
                fetchedAsset.assetTags?.map(tag => tag.tag).filter(Boolean) ??
                [];
            fetchedAsset.assetGroups = fetchedAsset.assetGroups?.map(group => ({
                id: group.populationGroupId,
            }));
            fetchedAsset.assetIndividuals = fetchedAsset.assetIndividuals?.map(
                individual => ({
                    id: individual.individualId,
                }),
            );
            setAsset(fetchedAsset);
            const isMyAssetsReadOnly =
                isMyAssets &&
                (fetchedAsset.assetTypeId === 1 ||
                    fetchedAsset.assetTypeId === 2 ||
                    user?.jwt.ind !== fetchedAsset.individualId);
            const isAssetLibraryReadOnly =
                !isMyAssets && fetchedAsset.assetTypeId === 3;
            const isAssetUploadGenerated =
                isMyAssets &&
                fetchedAsset.assetTypeId === 3 &&
                fetchedAsset.isLocked;
            setIsReadOnlyAsset(
                isMyAssetsReadOnly ||
                    isAssetLibraryReadOnly ||
                    (isResourceLibrary && isResourceLibraryReadOnly) ||
                    isAssetUploadGenerated ||
                    (fetchedAsset.isGlobalAsset && !isGlobalAssets),
            );
            setOriginalAsset(fetchedAsset);
        } catch (error) {
            console.error('Failed to fetch asset', error);
            Toast.error(getText('msg_b9f02f25f059'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (hasChanges()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_9b336403ea3c'),
                text: getText('msg_9bb770b4c894'),
                onYes: () => {
                    onClose(false);
                },
                onNo: () => {
                    return;
                },
                yesButtonText: getText('msg_36fff63ccbcd'),
                noButtonText: getText('msg_77dfd2135f4d'),
            });
        } else {
            onClose(false);
        }
    };

    useEffect(() => {
        if (isFullscreen) {
            return undefined;
        }

        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleClose, isFullscreen]);

    const hasChanges = () => {
        return (
            asset.name !== originalAsset.name ||
            replacementFile ||
            asset.description !== originalAsset.description ||
            JSON.stringify(asset.assetTags) !==
                JSON.stringify(originalAsset.assetTags) ||
            JSON.stringify(asset.assetIndividuals) !==
                JSON.stringify(originalAsset.assetIndividuals) ||
            JSON.stringify(asset.assetGroups) !==
                JSON.stringify(originalAsset.assetGroups) ||
            asset.isLocked !== originalAsset.isLocked ||
            asset.assetTypeId !== originalAsset.assetTypeId ||
            asset.overview !== originalAsset.overview ||
            asset.transcript !== originalAsset.transcript ||
            asset.isHidden !== originalAsset.isHidden ||
            asset.resourceScopeMask !== originalAsset.resourceScopeMask
        );
    };

    const handleSaveAsset = () => {
        // If the user typed something in TagInput but didn't press Enter/Space/comma,
        // flush() returns the pending value so we can append it to the existing tags.
        // We only use the flushed pending string — we do NOT replace asset.assetTags
        // wholesale, to avoid sync issues between TagInput's internal state and asset state.
        const pendingTag = tagInputRef.current?.flushPending();

        setIsSaving(true);

        if (!validateForm()) {
            setIsSaving(false);
            return;
        }

        let formattedAsset = { ...asset };
        // Append pending tag to existing tags if there is one
        formattedAsset.assetTags = pendingTag
            ? [...(asset.assetTags ?? []), pendingTag]
            : (asset.assetTags ?? []);
        formattedAsset.assetGroups =
            asset.assetGroups && asset.assetGroups.length > 0
                ? asset.assetGroups.map(group => group.id.toString())
                : [];
        formattedAsset.assetIndividuals =
            asset.assetIndividuals && asset.assetIndividuals.length > 0
                ? asset.assetIndividuals.map(individual =>
                      individual.id.toString(),
                  )
                : [];

        if (replacementFile) {
            const fileTypeAndSizeValidation =
                isValidFileTypeAndSize(replacementFile);
            if (fileTypeAndSizeValidation) {
                Toast.error(fileTypeAndSizeValidation);
                setIsSaving(false);
                return;
            }

            //Uploads and saves in the background so the drawer stays usable
            enqueueUpload(replacementFile.name, uploadOptions =>
                submitAssetUpdate({
                    replacementFile,
                    formattedAsset,
                    uploadOptions,
                }),
            ).catch(error => {
                console.error('An error occurred:', error);
            });

            //Tracker owns success/failure feedback from here; just confirm the queue
            Toast.info(getText('msg_9309271e92ff'));
        } else {
            //No file involved - save directly, no need to involve the upload tracker
            submitAssetUpdate({
                replacementFile: null,
                formattedAsset,
                uploadOptions: {},
            }).catch(error => {
                console.error('An error occurred:', error);
            });
        }

        onSaveOrDelete();
        setIsSaving(false);
    };

    const validateForm = () => {
        if (!asset.name || asset.name.trim() === '') {
            Toast.error(getText('msg_d7659e2c4af2'));
            return false;
        }
        if (asset.name.length > 256) {
            Toast.error(getText('msg_f4231db74e51'));
            return false;
        }
        if (asset.description && asset.description.length > 200) {
            Toast.error(getText('msg_04bc9f18737a'));
            return false;
        }
        if (
            isResourceLibrary &&
            (!asset.resourceScopeMask || asset.resourceScopeMask <= 0)
        ) {
            Toast.error(getText('msg_87b6d1d44d3e'));
            return false;
        }
        return true;
    };

    const handleClickOnDelete = () => {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_eee3584da320'),
            subtitle: getText('msg_874529bd5e14'),
            text: getText('msg_e24943ea4fc9'),
            onYes: () => {
                handleDeleteAsset();
            },
            onNo: () => {
                return;
            },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_f6fdbe48dc54'),
        });
        logEvent(LogEventType.DeleteClicked, `Asset delete clicked`);
    };

    const handleDeleteAsset = async () => {
        const response = await deleteAsset(asset.id, null);
        if (response.error) {
            Toast.error(response.message);
        } else if (response.status === 200) {
            Toast.success(getText('msg_43778d78002b'));
            onSaveOrDelete();
        } else {
            Toast.error(getText('msg_ae3d838fb88d'));
        }
    };

    const handleClickOnRemove = () => {
        showModal({
            type: ModalTypes.DELETE_CONFIRMATION,
            title: getText('msg_22544c87a3d0'),
            subtitle: getText('msg_7500298cd595'),
            text: getText('msg_0054145b43bf'),
            onYes: () => {
                handleRemoveAsset();
            },
            onNo: () => {
                return;
            },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_e963907dac5c'),
        });
        logEvent(LogEventType.DeleteClicked, `Asset remove clicked`);
    };

    const handleRemoveAsset = async () => {
        const response = await removeAsset(asset.id, null);
        if (response.error) {
            Toast.error(response.message);
        } else if (response.status === 200) {
            Toast.success(getText('msg_8d23ab557865'));
            onSaveOrDelete();
        } else {
            Toast.error(getText('msg_360dd0d530e2'));
        }
    };

    const getMetadataLabel = fileType => {
        const fileCategory = getFileCategory(fileType);
        if (fileCategory === 'audio' || fileCategory === 'movie') {
            return 'Run Time';
        } else if (fileCategory === 'image') {
            return 'Dimensions';
        }
        return null;
    };

    const handleContextMenuToggle = event => {
        event.stopPropagation();
        setIsContextMenuOpen(!isContextMenuOpen);
    };

    useEffect(() => {
        const handleClick = () => {
            if (isContextMenuOpen) {
                setIsContextMenuOpen(false);
            }
        };
        const handleScroll = () => {
            if (isContextMenuOpen) {
                setIsContextMenuOpen(false);
            }
        };
        document.addEventListener('click', handleClick);
        if (scrollableRef.current) {
            scrollableRef.current.addEventListener('scroll', handleScroll);
        }
        return () => {
            document.removeEventListener('click', handleClick);
            if (scrollableRef.current) {
                scrollableRef.current.removeEventListener(
                    'scroll',
                    handleScroll,
                );
            }
        };
    }, [isContextMenuOpen]);

    const openFullscreen = (mediaUrl, mediaType = 'image') => {
        setFullscreenImageUrl(mediaUrl);
        setFullscreenMediaType(mediaType);
        setIsFullscreen(true);
    };

    const closeFullscreen = () => {
        setIsFullscreen(false);
        setFullscreenImageUrl('');
        setFullscreenMediaType('image');
    };

    const downloadAsset = asset => {
        const processedName = processFileName(asset.name, asset.fileType);
        let source = asset.url;
        if (replacementImageSrc) {
            source = replacementImageSrc;
        }
        fetch(source)
            .then(response => {
                if (!response.ok) {
                    throw new Error('Network response failed');
                }
                return response.blob();
            })
            .then(blob => {
                const blobUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = blobUrl;
                a.download = processedName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
                logEvent(
                    LogEventType.UserDocumentDownloaded,
                    `Asset ${asset.name} downloaded`,
                    {
                        subjectDataTypeId: EventDataTypeEnum.Asset,
                        subjectId: asset.id,
                        subjectName: asset.name,
                        contextDataTypeId: eventContextDataTypeId,
                        contextId: eventContextId,
                        contextName: eventContextName,
                    },
                );
            })
            .catch(error => {
                console.error(
                    'There was a problem downloading the asset:',
                    error,
                );
            });
    };

    const isFileCategorySame = (newExtension, oldExtension) => {
        oldExtension = oldExtension.toLowerCase();
        newExtension = newExtension.toLowerCase();
        if (getFileCategory(newExtension) === getFileCategory(oldExtension)) {
            return true;
        } else {
            return false;
        }
    };

    const selectReplacementFile = event => {
        const newFile = event.target.files[0];
        const newFileExtension = getFileExtension(newFile);
        if (isFileCategorySame(newFileExtension, asset.fileType)) {
            setReplacementFile(newFile);
        } else {
            Toast.error(getText('msg_cf4193ced0f2'));
        }
        event.target.value = '';
    };

    const handleLabelClick = () => {
        showModal({
            type: ModalTypes.ACTION,
            title: getText('msg_b78130c92f32'),
            subtitle: getText('msg_ed5a540d079a'),
            text: getText('msg_75ca4eb982e4'),
            onYes: () => {
                const fileInput = document.getElementById('fileInput');
                fileInput.click();
            },
            onNo: () => {
                return;
            },
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_a7cf7b25a703'),
        });
    };

    useEffect(() => {
        const loadFileData = async () => {
            if (!replacementFile) return;
            const reader = new FileReader();
            reader.onloadend = () => {
                setReplacementImageSrc(reader.result);
            };
            reader.readAsDataURL(replacementFile);
            const fileCategory = getFileCategory(replacementFile.name);
            let metadata = '';
            if (fileCategory === 'video') {
                metadata = await getVideoRuntime(replacementFile);
            } else if (fileCategory === 'audio') {
                metadata = await getAudioLength(replacementFile);
            } else if (fileCategory === 'image') {
                metadata = await getImageDimensions(replacementFile);
            }
            setAsset(prev => ({
                ...prev,
                fileType: getFileExtension(replacementFile),
                fileSize: formatFileSize(replacementFile.size),
                metaData: metadata,
            }));
        };
        loadFileData();
    }, [replacementFile]);

    const handleOnAssetChange = (key, value) => {
        setAsset(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    const renderAssetTypeComponent = () => {
        const src = asset.url;
        const name = asset.name;
        const fileCategory = getFileCategory(asset.fileType);
        switch (fileCategory) {
            case 'image':
                return (
                    <img
                        src={replacementImageSrc || src}
                        alt={name}
                        className={styles.image}
                    />
                );
            case 'video':
                return (
                    <div className={styles.videoPreviewContainer}>
                        <video
                            src={replacementImageSrc || src}
                            className={styles.videoPreview}
                            preload="metadata"
                            muted
                            playsInline
                        />
                        <div className={styles.videoPreviewOverlay}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="40"
                                height="36"
                                viewBox="0 0 186 168"
                                fill="none"
                            >
                                <path
                                    d="M107.386 86.7865L81.9312 101.554C79.77 102.808 77 101.291 77 98.7688V69.2338C77 66.7113 79.77 65.1948 81.9312 66.4486L107.386 81.2161C109.538 82.4648 109.538 85.5378 107.386 86.7865Z"
                                    fill="#F5F9FF"
                                    fillOpacity="0.92"
                                />
                            </svg>
                        </div>
                    </div>
                );
            case 'audio':
                return (
                    <div className={styles.placeholderContainer}>
                        <svg
                            className={styles.placeholder}
                            xmlns="http://www.w3.org/2000/svg"
                            width="60"
                            height="60"
                            viewBox="-64 -54 166 148"
                            fill="none"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M21.25 5C21.9404 5 22.5 5.55964 22.5 6.25V33.75C22.5 34.4404 21.9404 35 21.25 35C20.5596 35 20 34.4404 20 33.75V6.25C20 5.55964 20.5596 5 21.25 5ZM16.25 10C16.9404 10 17.5 10.5596 17.5 11.25V28.75C17.5 29.4404 16.9404 30 16.25 30C15.5596 30 15 29.4404 15 28.75V11.25C15 10.5596 15.5596 10 16.25 10ZM26.25 10C26.9404 10 27.5 10.5596 27.5 11.25V28.75C27.5 29.4404 26.9404 30 26.25 30C25.5596 30 25 29.4404 25 28.75V11.25C25 10.5596 25.5596 10 26.25 10ZM11.25 13.75C11.9404 13.75 12.5 14.3096 12.5 15V25C12.5 25.6904 11.9404 26.25 11.25 26.25C10.5596 26.25 10 25.6904 10 25V15C10 14.3096 10.5596 13.75 11.25 13.75ZM31.25 13.75C31.9404 13.75 32.5 14.3096 32.5 15V25C32.5 25.6904 31.9404 26.25 31.25 26.25C30.5596 26.25 30 25.6904 30 25V15C30 14.3096 30.5596 13.75 31.25 13.75ZM6.25 16.25C6.94036 16.25 7.5 16.8096 7.5 17.5V22.5C7.5 23.1904 6.94036 23.75 6.25 23.75C5.55964 23.75 5 23.1904 5 22.5V17.5C5 16.8096 5.55964 16.25 6.25 16.25ZM36.25 16.25C36.9404 16.25 37.5 16.8096 37.5 17.5V22.5C37.5 23.1904 36.9404 23.75 36.25 23.75C35.5596 23.75 35 23.1904 35 22.5V17.5C35 16.8096 35.5596 16.25 36.25 16.25Z"
                                fill="var(--iNGAGED-Primary-Color)"
                                fillOpacity="0.4"
                            />
                        </svg>
                    </div>
                );
            case 'document':
            case 'spreadsheet':
            case 'other':
                return (
                    <div className={styles.placeholderContainer}>
                        <svg
                            className={styles.placeholder}
                            xmlns="http://www.w3.org/2000/svg"
                            width="30"
                            height="40"
                            viewBox="-168 -64 186 168"
                            fill="none"
                        >
                            <path
                                d="M-88 0H-74.7678C-74.1047 0 -73.4688 0.263392 -73 0.732233L-63.7322 10C-63.2634 10.4688 -63 11.1047 -63 11.7678V35C-63 37.7614 -65.2386 40 -68 40H-88C-90.7614 40 -93 37.7614 -93 35V5C-93 2.23858 -90.7614 0 -88 0ZM-74.25 3.75V8.75C-74.25 10.1307 -73.1307 11.25 -71.75 11.25H-66.75L-74.25 3.75Z"
                                fill="var(--iNGAGED-Primary-Color)"
                                fillOpacity="0.4"
                            />
                        </svg>
                    </div>
                );
            default:
                return null;
        }
    };

    const renderAssetDisplayMetadata = () => {
        return (
            <div className={styles.imageInfo}>
                <div className={styles.infoItem}>
                    <p>{getText('msg_3deb74565196')}</p>
                    <p className={styles['text-uppercase']}>{asset.fileType}</p>
                </div>
                <div className={styles.infoItem}>
                    <p>{getText('msg_b7152342a267')}</p>
                    <p>{asset.fileSize}</p>
                </div>
                {asset.metaData && (
                    <div className={styles.infoItem}>
                        <p>{getMetadataLabel(asset.fileType)}</p>
                        <p>{asset.metaData}</p>
                    </div>
                )}
            </div>
        );
    };

    const getAssetTypeTag = (typeId, isLocked) => {
        switch (typeId) {
            case 1:
                return {
                    tag: 'Content Only',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    backgroundColor: 'rgba(44, 110, 242, 0.2)',
                    textColor: '#136FFF',
                };
            case 2:
                return {
                    tag: 'Unassigned',
                    backgroundColor: '#D3D3D3',
                    textColor: '#4B4B4B',
                };
            case 3:
                return {
                    tag: 'Private',
                    backgroundColor: '#E0C6E0',
                    textColor: '#5A2D5B',
                };
            default:
                return {
                    tag: 'Unknown',
                    backgroundColor: '#D3D3D3',
                    textColor: '#A9A9A9',
                };
        }
    };

    return (
        <>
            <div css={overlayStyles} onClick={handleClose} />
            <div className={styles.startBelowHeader} css={drawerStyles}>
                {isLoading || isSaving ? (
                    <Spinner width={50} className={styles.loadingSpinner} />
                ) : (
                    <div className="d-flex w-100 h-100 flex-direction-column">
                        <FullscreenDisplay
                            isFullscreen={isFullscreen}
                            fullscreenImageUrl={fullscreenImageUrl}
                            mediaType={fullscreenMediaType}
                            closeFullscreen={closeFullscreen}
                        />
                        <input
                            type="file"
                            id="fileInput"
                            onChange={event => selectReplacementFile(event)}
                            hidden
                        />
                        <div
                            className={`d-flex mb-5 justify-content-between flex-nowrap w-100 ${isMobileView ? '' : 'mt-5'} ${styles['padding-horizontal-20']}`}
                        >
                            {isHistoryPanelVisible ? (
                                <span
                                    type="button"
                                    onClick={() =>
                                        setIsHistoryPanelVisible(false)
                                    }
                                    className={`ing-text-buttons d-flex align-items-center ${styles['gap-8']}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9.93436 1.44064C10.1052 1.6115 10.1052 1.8885 9.93436 2.05936L4.99372 7L9.93436 11.9406C10.1052 12.1115 10.1052 12.3885 9.93436 12.5594C9.7635 12.7302 9.4865 12.7302 9.31564 12.5594L4.06564 7.30936C3.89479 7.1385 3.89479 6.8615 4.06564 6.69064L9.31564 1.44064C9.4865 1.26979 9.7635 1.26979 9.93436 1.44064Z"
                                            fill="var(--iNGAGED-Neutral-Color2)"
                                        />
                                    </svg>
                                    {getText('msg_b52b36b7269f')}
                                </span>
                            ) : isPortalsPanelVisible ? (
                                <span
                                    type="button"
                                    onClick={() =>
                                        setIsPortalsPanelVisible(false)
                                    }
                                    className={`ing-text-buttons d-flex align-items-center ${styles['gap-8']}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9.93436 1.44064C10.1052 1.6115 10.1052 1.8885 9.93436 2.05936L4.99372 7L9.93436 11.9406C10.1052 12.1115 10.1052 12.3885 9.93436 12.5594C9.7635 12.7302 9.4865 12.7302 9.31564 12.5594L4.06564 7.30936C3.89479 7.1385 3.89479 6.8615 4.06564 6.69064L9.31564 1.44064C9.4865 1.26979 9.7635 1.26979 9.93436 1.44064Z"
                                            fill="var(--iNGAGED-Neutral-Color2)"
                                        />
                                    </svg>
                                    {getText('msg_b52b36b7269f')}
                                </span>
                            ) : isNotificationsPanelVisible ? (
                                <span
                                    type="button"
                                    onClick={() =>
                                        setIsNotificationsPanelVisible(false)
                                    }
                                    className={`ing-text-buttons d-flex align-items-center ${styles['gap-8']}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9.93436 1.44064C10.1052 1.6115 10.1052 1.8885 9.93436 2.05936L4.99372 7L9.93436 11.9406C10.1052 12.1115 10.1052 12.3885 9.93436 12.5594C9.7635 12.7302 9.4865 12.7302 9.31564 12.5594L4.06564 7.30936C3.89479 7.1385 3.89479 6.8615 4.06564 6.69064L9.31564 1.44064C9.4865 1.26979 9.7635 1.26979 9.93436 1.44064Z"
                                            fill="var(--iNGAGED-Neutral-Color2)"
                                        />
                                    </svg>
                                    {getText('msg_b52b36b7269f')}
                                </span>
                            ) : isTemplatesPanelVisible ? (
                                <span
                                    type="button"
                                    onClick={() =>
                                        setIsTemplatesPanelVisible(false)
                                    }
                                    className={`ing-text-buttons d-flex align-items-center ${styles['gap-8']}`}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 14 14"
                                        fill="none"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            clipRule="evenodd"
                                            d="M9.93436 1.44064C10.1052 1.6115 10.1052 1.8885 9.93436 2.05936L4.99372 7L9.93436 11.9406C10.1052 12.1115 10.1052 12.3885 9.93436 12.5594C9.7635 12.7302 9.4865 12.7302 9.31564 12.5594L4.06564 7.30936C3.89479 7.1385 3.89479 6.8615 4.06564 6.69064L9.31564 1.44064C9.4865 1.26979 9.7635 1.26979 9.93436 1.44064Z"
                                            fill="var(--iNGAGED-Neutral-Color2)"
                                        />
                                    </svg>
                                    {getText('msg_b52b36b7269f')}
                                </span>
                            ) : (
                                <div
                                    className={`d-flex justify-content-center align-items-center ${styles['gap-16']}`}
                                >
                                    {asset.isLocked && !isPortalActions && (
                                        <i
                                            className={`bi bi-lock-fill ${styles.lockIconNeutral}`}
                                            title={getText('msg_a798882f1c31')}
                                        />
                                    )}
                                    {[1, 2, 3].includes(asset.assetTypeId) && (
                                        <>
                                            <div
                                                className={`d-flex justify-content-center align-items-center ${styles.assetTypeTag}`}
                                                style={{
                                                    background: getAssetTypeTag(
                                                        asset.assetTypeId,
                                                        asset.isLocked,
                                                    ).backgroundColor,
                                                }}
                                            >
                                                <p
                                                    className={`text-center m-0 ${styles.assetTypeTagText}`}
                                                    style={{
                                                        color: getAssetTypeTag(
                                                            asset.assetTypeId,
                                                            asset.isLocked,
                                                        ).textColor,
                                                    }}
                                                >
                                                    {
                                                        getAssetTypeTag(
                                                            asset.assetTypeId,
                                                            asset.isLocked,
                                                        ).tag
                                                    }
                                                </p>
                                            </div>
                                            {isMyAssets ? (
                                                user?.jwt.ind ===
                                                asset.individualId ? (
                                                    asset.assetIndividuals
                                                        .length > 0 ? (
                                                        // Outward icon ("shared by me")
                                                        <svg
                                                            width="23"
                                                            height="18"
                                                            viewBox="0 0 23 18"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M8.75 15C8.75 15 7.5 15 7.5 13.75C7.5 12.5 8.75 8.75 13.75 8.75C18.75 8.75 20 12.5 20 13.75C20 15 18.75 15 18.75 15H8.75Z"
                                                                fill="#1366FF"
                                                            />
                                                            <path
                                                                d="M13.75 7.5C15.8211 7.5 17.5 5.82107 17.5 3.75C17.5 1.67893 15.8211 0 13.75 0C11.6789 0 10 1.67893 10 3.75C10 5.82107 11.6789 7.5 13.75 7.5Z"
                                                                fill="#1366FF"
                                                            />
                                                            <path
                                                                d="M6.52045 15C6.34445 14.6449 6.25 14.2239 6.25 13.75C6.25 12.0557 7.09882 10.3132 8.66991 9.09996C7.98262 8.88012 7.18137 8.75 6.25 8.75C1.25 8.75 0 12.5 0 13.75C0 15 1.25 15 1.25 15H6.52045Z"
                                                                fill="#1366FF"
                                                            />
                                                            <path
                                                                d="M5.625 7.5C7.35089 7.5 8.75 6.10089 8.75 4.375C8.75 2.64911 7.35089 1.25 5.625 1.25C3.89911 1.25 2.5 2.64911 2.5 4.375C2.5 6.10089 3.89911 7.5 5.625 7.5Z"
                                                                fill="#1366FF"
                                                            />
                                                            <circle
                                                                cx="19"
                                                                cy="14"
                                                                r="3.75"
                                                                fill="#1366FF"
                                                                stroke="white"
                                                                strokeWidth="0.5"
                                                            />
                                                            <path
                                                                d="M17.0158 14.0321C17.0173 13.8929 17.1305 13.7813 17.2685 13.7827L20.1648 13.8136L19.1031 12.7204C19.0066 12.621 19.0083 12.4615 19.1069 12.3641C19.2056 12.2667 19.3639 12.2684 19.4605 12.3679L20.9443 13.8957C21.0409 13.9951 21.0392 14.1547 20.9405 14.252L19.4245 15.7479C19.3258 15.8453 19.1675 15.8436 19.071 15.7442C18.9744 15.6447 18.9761 15.4852 19.0748 15.3878L20.1594 14.3175L17.2631 14.2867C17.1251 14.2852 17.0144 14.1712 17.0158 14.0321Z"
                                                                fill="white"
                                                            />
                                                        </svg>
                                                    ) : null
                                                ) : (
                                                    // Inward icon ("shared to me")
                                                    <svg
                                                        width="23"
                                                        height="18"
                                                        viewBox="0 0 23 18"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M8.75 15C8.75 15 7.5 15 7.5 13.75C7.5 12.5 8.75 8.75 13.75 8.75C18.75 8.75 20 12.5 20 13.75C20 15 18.75 15 18.75 15H8.75Z"
                                                            fill="#1366FF"
                                                        />
                                                        <path
                                                            d="M13.75 7.5C15.8211 7.5 17.5 5.82107 17.5 3.75C17.5 1.67893 15.8211 0 13.75 0C11.6789 0 10 1.67893 10 3.75C10 5.82107 11.6789 7.5 13.75 7.5Z"
                                                            fill="#1366FF"
                                                        />
                                                        <path
                                                            d="M6.52045 15C6.34445 14.6449 6.25 14.2239 6.25 13.75C6.25 12.0557 7.09882 10.3132 8.66991 9.09996C7.98262 8.88012 7.18137 8.75 6.25 8.75C1.25 8.75 0 12.5 0 13.75C0 15 1.25 15 1.25 15H6.52045Z"
                                                            fill="#1366FF"
                                                        />
                                                        <path
                                                            d="M5.625 7.5C7.35089 7.5 8.75 6.10089 8.75 4.375C8.75 2.64911 7.35089 1.25 5.625 1.25C3.89911 1.25 2.5 2.64911 2.5 4.375C2.5 6.10089 3.89911 7.5 5.625 7.5Z"
                                                            fill="#1366FF"
                                                        />
                                                        <circle
                                                            cx="19"
                                                            cy="14"
                                                            r="3.75"
                                                            transform="rotate(-180 19 14)"
                                                            fill="#1366FF"
                                                            stroke="white"
                                                            strokeWidth="0.5"
                                                        />
                                                        <path
                                                            d="M20.9842 13.9679C20.9827 14.1071 20.8695 14.2187 20.7315 14.2173L17.8352 14.1864L18.8969 15.2796C18.9934 15.379 18.9917 15.5385 18.8931 15.6359C18.7944 15.7333 18.6361 15.7316 18.5395 15.6321L17.0557 14.1043C16.9591 14.0049 16.9608 13.8453 17.0595 13.748L18.5755 12.2521C18.6742 12.1547 18.8325 12.1564 18.929 12.2558C19.0256 12.3553 19.0239 12.5148 18.9252 12.6122L17.8406 13.6825L20.7369 13.7133C20.8749 13.7148 20.9856 13.8288 20.9842 13.9679Z"
                                                            fill="white"
                                                        />
                                                    </svg>
                                                )
                                            ) : asset.assetGroups.length > 0 ||
                                              asset.assetIndividuals.length >
                                                  0 ? (
                                                // Generic shared icon
                                                <i
                                                    className={`bi bi-people-fill ${styles.sharedIconColor}`}
                                                    title={getText(
                                                        'msg_50d0d8dd11a9',
                                                    )}
                                                ></i>
                                            ) : null}
                                        </>
                                    )}
                                    {asset.isGlobalAsset &&
                                        asset.assetTypeId != 6 && (
                                            <i
                                                className={`bi bi-globe ${styles.globalAssetIconStyle}`}
                                                title={getText(
                                                    'msg_b4c5d6e7f8a9',
                                                )}
                                            />
                                        )}
                                </div>
                            )}
                            <span
                                type="button"
                                onClick={() => handleClose()}
                                className={`ing-text-buttons d-flex align-items-center justify-content-center ${styles['gap-6']}`}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="21"
                                    height="21"
                                    viewBox="0 0 21 21"
                                    fill="none"
                                >
                                    <path
                                        d="M5.99739 5.93745C6.24147 5.69338 6.6372 5.69338 6.88127 5.93745L10.1893 9.24551L13.4974 5.93745C13.7415 5.69338 14.1372 5.69338 14.3813 5.93745C14.6254 6.18153 14.6254 6.57726 14.3813 6.82134L11.0732 10.1294L14.3813 13.4375C14.6254 13.6815 14.6254 14.0773 14.3813 14.3213C14.1372 14.5654 13.7415 14.5654 13.4974 14.3213L10.1893 11.0133L6.88127 14.3213C6.6372 14.5654 6.24147 14.5654 5.99739 14.3213C5.75331 14.0773 5.75331 13.6815 5.99739 13.4375L9.30545 10.1294L5.99739 6.82134C5.75331 6.57726 5.75331 6.18153 5.99739 5.93745Z"
                                        fill="var(--iNGAGED-Neutral-Color2)"
                                    />
                                </svg>
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                        <div
                            ref={scrollableRef}
                            className={`${styles['padding-horizontal-20']} ${styles.scrollableContent}`}
                        >
                            <div
                                className={`${styles.imageContainer} ${styles['margin-bottom-24']}`}
                            >
                                {renderAssetTypeComponent()}
                                {!isAssetLibraryReadOnly && (
                                    <button
                                        className={styles.moreActionsBtn}
                                        onClick={e =>
                                            handleContextMenuToggle(e)
                                        }
                                    >
                                        <i className="bi bi-three-dots"></i>
                                    </button>
                                )}
                                <div
                                    className={classNames(styles.contextMenu, {
                                        [styles.tabPanelHidden]:
                                            !isContextMenuOpen,
                                    })}
                                >
                                    {(() => {
                                        const assetCategory = getFileCategory(
                                            asset.fileType,
                                        );
                                        const canRenderDocumentInBrowser =
                                            assetCategory === 'document' &&
                                            canRenderAssetPreviewInBrowser(
                                                asset.fileType,
                                                process.env
                                                    .REACT_APP_DEPLOYED_ENV_NAME,
                                            );

                                        const canShowViewAction =
                                            assetCategory === 'image' ||
                                            assetCategory === 'video' ||
                                            canRenderDocumentInBrowser;

                                        return (
                                            <ul>
                                                {/* or video */}
                                                {canShowViewAction && (
                                                    <li
                                                        onClick={() => {
                                                            if (
                                                                assetCategory ===
                                                                'image'
                                                            ) {
                                                                openFullscreen(
                                                                    replacementImageSrc ||
                                                                        asset.url,
                                                                    'image',
                                                                );
                                                            } else if (
                                                                assetCategory ===
                                                                'video'
                                                            ) {
                                                                openFullscreen(
                                                                    replacementImageSrc ||
                                                                        asset.url,
                                                                    'video',
                                                                );
                                                            } else {
                                                                showModal({
                                                                    type: ModalTypes.ASSET_VIEW,
                                                                    title: asset.name,
                                                                    text: asset.url,
                                                                    fileType:
                                                                        asset.fileType,
                                                                });
                                                            }
                                                            logEvent(
                                                                LogEventType.UserDocumentViewed,
                                                                `Asset ${asset.name} viewed`,
                                                                {
                                                                    subjectDataTypeId:
                                                                        EventDataTypeEnum.Asset,
                                                                    subjectId:
                                                                        asset.id,
                                                                    subjectName:
                                                                        asset.name,
                                                                    contextDataTypeId:
                                                                        eventContextDataTypeId,
                                                                    contextId:
                                                                        eventContextId,
                                                                    contextName:
                                                                        eventContextName,
                                                                },
                                                            );
                                                        }}
                                                    >
                                                        {getText(
                                                            'msg_84ecac328f77',
                                                        )}
                                                    </li>
                                                )}
                                                {!isReadOnlyAsset &&
                                                    !isAssetLibraryReadOnly && (
                                                        <li
                                                            onClick={
                                                                handleLabelClick
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_a7cf7b25a703',
                                                            )}
                                                        </li>
                                                    )}
                                                <li
                                                    onClick={() =>
                                                        downloadAsset(asset)
                                                    }
                                                >
                                                    {getText(
                                                        'msg_a479c9c34e87',
                                                    )}
                                                </li>
                                            </ul>
                                        );
                                    })()}
                                </div>
                                {!isHistoryPanelVisible &&
                                    !isPortalsPanelVisible &&
                                    !isNotificationsPanelVisible &&
                                    !isTemplatesPanelVisible &&
                                    renderAssetDisplayMetadata()}
                            </div>
                            {(!isReadOnlyAsset || isResourceLibrary) &&
                                !isHistoryPanelVisible &&
                                !isPortalsPanelVisible &&
                                !isNotificationsPanelVisible &&
                                !isTemplatesPanelVisible && (
                                    <AssetDetailsPanel
                                        asset={asset}
                                        isMyAssets={isMyAssets}
                                        onNavigateToHistory={() =>
                                            setIsHistoryPanelVisible(true)
                                        }
                                        onNavigateToPortals={() =>
                                            setIsPortalsPanelVisible(true)
                                        }
                                        onNavigateToNotifications={() =>
                                            setIsNotificationsPanelVisible(true)
                                        }
                                        onNavigateToTemplates={() =>
                                            setIsTemplatesPanelVisible(true)
                                        }
                                        onChangeAsset={handleOnAssetChange}
                                        isGlobalAssets={isGlobalAssets}
                                        tagInputRef={tagInputRef}
                                        isAssetLibraryReadOnly={
                                            isAssetLibraryReadOnly ||
                                            (isResourceLibrary &&
                                                isResourceLibraryReadOnly)
                                        }
                                        isResourceLibrary={isResourceLibrary}
                                        allowAssetSharing={allowAssetSharing}
                                    />
                                )}
                            {isReadOnlyAsset && !isResourceLibrary && (
                                <AssetReadOnlyPanel asset={asset} />
                            )}
                            {isHistoryPanelVisible && (
                                <AssetHistoryPanel asset={asset} />
                            )}
                            {isPortalsPanelVisible && (
                                <AssetPortalsPanel asset={asset} />
                            )}
                            {isNotificationsPanelVisible && (
                                <AssetNotificationsPanel asset={asset} />
                            )}
                            {isTemplatesPanelVisible && (
                                <AssetTemplatesPanel asset={asset} />
                            )}
                        </div>
                        {canEditAsset &&
                            !isHistoryPanelVisible &&
                            !isPortalsPanelVisible &&
                            !isNotificationsPanelVisible &&
                            !isTemplatesPanelVisible && (
                                <div
                                    className={`d-flex justify-content-space-between ${styles['padding-top-20']}`}
                                >
                                    <button
                                        type="button"
                                        className="float-start button-base ing-popup-red-negative"
                                        onClick={() => handleClickOnDelete()}
                                    >
                                        {getText('msg_f6fdbe48dc54')}
                                    </button>
                                    <button
                                        type="button"
                                        className="float-end button-base ing-button-primary-left"
                                        onClick={() => handleSaveAsset()}
                                    >
                                        {getText('msg_efc007a393f6')}
                                    </button>
                                </div>
                            )}
                        {isReadOnlyAsset &&
                            isMyAssets &&
                            user?.jwt.ind !== asset.individualId &&
                            (asset.assetIndividuals || []).some(
                                ind => ind.id === user?.jwt.ind,
                            ) && (
                                <div
                                    className={`d-flex justify-content-end ${styles['padding-top-20']}`}
                                >
                                    <button
                                        type="button"
                                        className="float-end button-base ing-popup-red-negative"
                                        onClick={() => handleClickOnRemove()}
                                    >
                                        {getText('msg_e963907dac5c')}
                                    </button>
                                </div>
                            )}
                    </div>
                )}
            </div>
        </>
    );
};

export default AssetDrawer;
